import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { combineLatest, defer, merge, Observable, Subject, switchMap } from 'rxjs';
import { MatSort, Sort } from '@angular/material/sort';
import { MatSidenav } from '@angular/material/sidenav';
import { InvoicePdfService } from '../service/invoice-pdf.service';
import { PayableTableObject } from '../models/payable-table-object';
import { EmptyPageService } from '../empty-page/empty-page.service';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, skip, take, takeUntil, tap } from 'rxjs/operators';
import { ConfigService, ConfigState, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { Store } from '@ngxs/store';
import {
  ClearArTranAllocations,
  FillPartialArTranAllocations,
  PaymentsState,
  PaymentTrackService,
  PaymentWorkflowEvent,
  RemoveArTranAllocations,
  SetArTranAllocations,
  UpdateArTranAllocations,
} from '@aiwyn/common/payments';
import { ArTranAllocation } from '@aiwyn/api';
import {
  AiwynBreakpoint,
  distinctUntilObjectChanged,
  fromSync,
  getFirstOfQueryList,
  getLoadedResults,
  ignoreEmpty,
  ignoreNullish,
  insertItemIntoArray,
  invert,
  isEmpty,
  mapToAndResult,
  removeArrayItem,
  tapOnFirstEmission,
  TrackService,
} from '@aiwyn/shared';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { CommonSettingsState } from '@aiwyn/common/settings';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AutopayEligibilityService } from '@aiwyn/services';

const DUE_UPON_RECEIPT = 'Due Upon Receipt';

enum SelectAllCheckboxStates {
  ALL = 'ALL',
  SOME = 'SOME',
  NONE = 'NONE',
}

@Component({
  selector: 'app-unpaid',
  templateUrl: './unpaid.component.html',
  styleUrls: ['./unpaid.component.scss'],
  standalone: false,
})
export class UnpaidComponent implements AfterViewInit, OnDestroy {
  DUE_UPON_RECEIPT = DUE_UPON_RECEIPT;

  _unsubscribe$ = new Subject<void>();

  // Sort for material table.
  @ViewChildren(MatSort) sort: QueryList<MatSort>;
  @ViewChild(MatTable) table: MatTable<PayableTableObject>;

  // Material sidenav.
  @ViewChild('sidenav') public sidenav: MatSidenav;

  // Search string for global search.
  searchString = input<string>();
  searchString$ = toObservable(this.searchString);

  // PV5
  @Output() onPayWithoutInvoice = new EventEmitter<void>();

  // Select all checkbox behavior
  // This checkbox can behave in different ways depending on whether or not there are multiple business entities present:
  // -- If only one business entity is present in the data, the checkbox will behave as a normal `select all` checkbox.
  // -- If multiple business entities are present in the data, the checkbox will behave as a `select all` checkbox, but will be indeterminate
  //    and will select all invoices that belong to the entity that has the most invoices pending.  In this case it will only render as
  //    'unchecked' or 'indeterminate'.
  checkboxStates = SelectAllCheckboxStates;
  areMultipleBusinessEntitiesPresent = this.store.selectSignal(PaymentsState.areMultipleBusinessEntitiesPresent);
  areAllVisiblePayablesSelected$: Observable<SelectAllCheckboxStates> = combineLatest([
    this.store.select(PaymentsState.getSelectedPayableIds),
    this.store.select(PaymentsState.areMultipleBusinessEntitiesPresent),
    this.searchString$,
  ]).pipe(
    debounceTime(100),
    map(([selectedPayableIds, areMultipleBusinessEntitiesPresent]) => {
      if (areMultipleBusinessEntitiesPresent && selectedPayableIds.length > 0) {
        return SelectAllCheckboxStates.SOME;
      }

      if (selectedPayableIds.length === 0) {
        return SelectAllCheckboxStates.NONE;
      }

      const filteredPayables = this.unpaidPayablesDataSource.filteredData;
      return !isEmpty(filteredPayables) && filteredPayables.every(({ arTranId }) => selectedPayableIds.includes(arTranId))
        ? SelectAllCheckboxStates.ALL
        : SelectAllCheckboxStates.NONE;
    }),
    shareReplay(1),
  );

  // Columns for material table.
  columnsToDisplay = ['invoiceChecked', 'entityName', 'clientName', 'invoiceNumber', 'dueDate', 'amount', 'displayStatus', 'invoiceMenu'];


  unpaidPayablesDataSource = new MatTableDataSource<PayableTableObject>();
  payables$ = getLoadedResults(this.store.select(PaymentsState.getPayablesLoadable)).pipe(
    shareReplay(1),
  );

  selectedPayables = this.store.selectSignal(PaymentsState.getSelectedPayables);

  fillPartialArTranAllocationsOnInit$ = combineLatest([
    this.payables$,
    this.store.select(PaymentsState.getArTranAllocations),
  ]).pipe(
    take(1),
    filter(([payables, arTranAllocations]) => {
      if (isEmpty(arTranAllocations)) {
        return false;
      }

      return arTranAllocations.some(allocation =>
        allocation.allocatedAmount < payables.find(({ arTranId }) => arTranId === allocation.arTranId).amountRemaining,
      );
    }),
    tap(_ => this.store.dispatch(new FillPartialArTranAllocations())),
    takeUntil(this._unsubscribe$),
  );

  isPayWithInvoices$: Observable<boolean> = this.store.select(PaymentsState.getPayWithInvoices);

  selectedInvoicesWithAttachmentsCount$ = this.store.select(PaymentsState.getSelectedPayables).pipe(
    map(payables => (payables ?? []).filter(({ invoice }) => invoice.invoiceFileId).length),
    shareReplay(),
  );

  doUnpaidPayablesExist = this.payables$.pipe(
    map(payables => !isEmpty(payables.filter(({ amountRemaining }) => amountRemaining > 0))),
    shareReplay(1),
  );

  toggleEntityNameColumnVisibility$ = this.store.select(PaymentsState.areMultipleBusinessEntitiesPresent).pipe(
    ignoreNullish(),
    distinctUntilChanged(),
    tapOnFirstEmission(areMultipleBusinessEntitiesPresent => {
      if (areMultipleBusinessEntitiesPresent) {
        this.trackService.track('Multiple Business Entities');
      }
    }),
    tap(areMultipleBusinessEntitiesPresent => {
      if (!areMultipleBusinessEntitiesPresent) {
        const entityNameColumnIndex = this.columnsToDisplay.findIndex(columnName => columnName === 'entityName');
        if (entityNameColumnIndex !== -1) {
          this.columnsToDisplay = removeArrayItem(this.columnsToDisplay, entityNameColumnIndex);
        }

        return;
      }

      if (!this.columnsToDisplay.includes('entityName')) {
        this.columnsToDisplay = insertItemIntoArray(this.columnsToDisplay, 1, 'entityName');
      }
    }),
    takeUntil(this._unsubscribe$),
  );

  syncTableState$ = merge(
    // row data
    this.payables$.pipe(
      tap(payables => this.unpaidPayablesDataSource.data = payables),
      switchMap(_ => getFirstOfQueryList(this.sort).pipe(
        take(1),
        tap(_ => this.setDefaultSort()),
      )),
    ),

    // selections
    getLoadedResults(this.store.select(PaymentsState.getPayablesLoadable)).pipe(
      take(1),
      switchMap(_ => fromSync(() => this.unpaidPayablesDataSource.data)),
      switchMap(_ => this.store.select(PaymentsState.getSelectedPayableIds)),
      distinctUntilObjectChanged(),
      tap(selectedPayableIds => this.unpaidPayablesDataSource.data.forEach(payable =>
        payable.checked = selectedPayableIds.includes(payable.arTranId),
      )),
    ),

    // disable/enable rows (If multiple business entities are present, only one can be selected at a time. This is a payment restriction.)
    this.store.select(PaymentsState.getSelectedBusinessEntity()).pipe(
      map(selectedBusinessEntity => selectedBusinessEntity?.id),
      distinctUntilChanged(),
      skip(1),
      tap(selectedEntityId => {
        const defaultEntityId = this.store.selectSnapshot(ConfigState.getDefaultBusinessEntity).id;
        this.unpaidPayablesDataSource.data.forEach(
          selectedEntityId
            ? payable => payable.disabled = defaultEntityId && (payable.invoice.arTran.businessEntityId ?? defaultEntityId) !== selectedEntityId
            : payable => payable.disabled = false,
        );
      }),
    ),
  ).pipe(
    takeUntil(this._unsubscribe$),
  );

  selectDefaultPayablesOnInit$ = this.payables$.pipe(
    take(1),
    ignoreEmpty(),
    tap(_ => {
      // Payable selection may occur before navigating to this page, so ensure it's emitted AFTER page load so funnels still catch it.
      const allocations = this.store.selectSnapshot(PaymentsState.getArTranAllocations);
      if (!isEmpty(allocations)) {
        this.paymentTrackService.trackWorkflow(
          PaymentWorkflowEvent.PAYMENT_PAYABLES_SELECTED,
          {
            ...PaymentsState.buildPaymentEventBase(this.store),
            subtotal: this.store.selectSnapshot(PaymentsState.getArTranAllocationsTotal) ?? 0,
            payableCount: allocations.length,
            // PP-3775: rehydration of a pre-existing selection on page load — not active intent.
            intentTrigger: 'auto',
          },
        );
      }
    }),
    filter(_ => this.configService.selectUnpaidInvoicesByDefault && isEmpty(this.store.selectSnapshot(PaymentsState.getArTranAllocations))),
    map(payables => {
      const entityIdWithMostAmountDue = this.store.selectSnapshot(PaymentsState.getBusinessEntityIdWithMostAmountDue);
      const matchingPayables = payables.filter(({ entityId }) => entityId === entityIdWithMostAmountDue);
      return matchingPayables.map(payable => ({
        arTranId: payable.arTranId,
        allocatedAmount: payable.amountRemaining,
        active: payable.invoice.arTran.active,
      }));
    }),
    switchMap(arTranAllocations => this.store.dispatch(new SetArTranAllocations(arTranAllocations))),
  );

  isMobile$: Observable<boolean> = this.breakpointObserver.observe([AiwynBreakpoint.SIDEBAR_POPOVER]).pipe(
    map(breakpointMatched => breakpointMatched.matches),
    shareReplay(1),
  );

  shouldShowInvoicesTableSummary$ = defer(() => combineLatest([
    this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES),
    this.isMobile$.pipe(invert()),
    this.store.select(CommonSettingsState.doMultipleClientCurrenciesExist).pipe(
      ignoreNullish(),
      invert(),
    ),
  ])).pipe(
    mapToAndResult(),
  );

  private readonly paymentTrackService = inject(PaymentTrackService);
  readonly autopayEligibilityService = inject(AutopayEligibilityService);

  constructor(
    public dialog: MatDialog,
    public emptyPageService: EmptyPageService,
    public configService: ConfigService,
    public invoicePdfService: InvoicePdfService,
    private changeDetectorRef: ChangeDetectorRef,
    private store: Store,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly trackService: TrackService,
  ) {
    this.fillPartialArTranAllocationsOnInit$.subscribe();
    this.selectDefaultPayablesOnInit$.subscribe();
    this.toggleEntityNameColumnVisibility$.subscribe();

    effect(() => this.applyFilter(this.searchString()));
  }

  ngAfterViewInit(): void {
    this.unpaidPayablesDataSource.filterPredicate = UnpaidComponent.filterPredicate;
    this.unpaidPayablesDataSource.sortingDataAccessor = UnpaidComponent.sortingDataAccessor;

    this.syncTableState$.subscribe();
  }

  /**
   * Does table row match search?
   * @param payable
   * @param filter automatically converted to lowercase
   */
  static filterPredicate(payable: PayableTableObject, filter: string): boolean {
    const searchableValues = [
      payable.invoiceNumber,
      `${payable.invoice.arTran.amount}`,
      payable.dueDateDisplay,
      payable.statusSearchString,
      payable.displayStatus,
      payable.clientName,
    ].filter(Boolean);

    return searchableValues.some(value => value.toLowerCase().includes(filter));
  }

  /**
   * Used to implement search functionality with input from invoices.component.
   */
  applyFilter(filterValue: string): void {
    this.unpaidPayablesDataSource.filter = filterValue ? filterValue.trim().toLowerCase() : '';
  }

  static sortingDataAccessor(payable: PayableTableObject, sortHeaderId: string): string | number {
    switch (sortHeaderId) {
      case 'invoiceChecked':
        return payable.checked ? 1 : 0;
      case 'entityName':
        return payable.entityName;
      case 'clientName':
        return payable.clientName;
      case 'invoiceNumber':
        return Number.isNaN(+payable.invoice.invoiceNumber) ? payable.invoice.invoiceNumber : +payable.invoice.invoiceNumber;
      case 'dueDate':
        return payable.invoice.dueDate.toISOString();
      case 'amount':
        return payable.amountRemaining;
      case 'displayStatus':
        return `${UnpaidComponent.getFirstStatusSortPart(payable)}_${payable.dueDate.toISOString()}`;
    }
  }

  static getFirstStatusSortPart(payable: PayableTableObject): `${0 | 1 | 2 | 3}` {
    if (payable.overdue) {
      return '0';
    }

    if (isEmpty(payable.autoExecutionDate)) {
      return '1';
    }

    return payable.partialPayment ? '2' : '3';
  }

  /**
   * Set default sort based on application configuration.
   */
  setDefaultSort(): void {
    if (this.configService.isMobile || this.sort == undefined) {
      return;
    }

    this.unpaidPayablesDataSource.sort = this.sort.first;
    const sortState: Sort = {
      active: this.areMultipleBusinessEntitiesPresent() ? 'dueDate' : 'entityName',
      direction: 'asc',
    };

    this.sort.first.active = sortState.active;
    this.sort.first.direction = sortState.direction;
    this.changeDetectorRef.detectChanges();
  }

  /**
   * updateCheck is called upon clicking 'select all' checkbox sends dataSource to checkAllInvoices.
   */
  onHeaderCheckboxChange(change: MatCheckboxChange): void {

    if (this.areMultipleBusinessEntitiesPresent()) {
      this.areAllVisiblePayablesSelected$.pipe(
        take(1),
        map(state => {
          if (state === SelectAllCheckboxStates.SOME || !change.checked) {
            // MatCheckbox default behavior will show a checked state coming from indeterminate, so override it.
            change.source.checked = false;
            return this.store.dispatch(new ClearArTranAllocations());
          }

          const invoiceCountsByEntity = this.unpaidPayablesDataSource.filteredData.reduce((acc, item) => {
            acc[item.entityId] = (acc[item.entityId] || 0) + 1;
            return acc;
          }, {});

          const entityIdWithMostInvoices = Object.keys(invoiceCountsByEntity)
            .reduce((a, b) => invoiceCountsByEntity[a] > invoiceCountsByEntity[b] ? a : b);

          const arTranAllocations = this.unpaidPayablesDataSource.filteredData
            .filter(invoice => invoice.entityId === +entityIdWithMostInvoices)
            .map(payable => ({
              arTranId: payable.arTranId,
              allocatedAmount: payable.amountRemaining,
              active: payable.invoice.arTran.active,
            }));

          return this.store.dispatch(new SetArTranAllocations(arTranAllocations));

        }),
      ).subscribe();

    } else {
      if (!change.checked) {
        this.store.dispatch(new ClearArTranAllocations());
        return;
      }

      const arTranAllocations = this.unpaidPayablesDataSource.filteredData.map(payable => ({
        arTranId: payable.arTranId,
        allocatedAmount: payable.amountRemaining,
        active: payable.invoice.arTran.active,
      }));

      this.store.dispatch(new SetArTranAllocations(arTranAllocations));
    }

  }

  /**
   * Assists the selectionModel to allow the user to click any part of a row to select an invoice.
   *
   * V5 Notes: Follows AC from https://aiwyn.atlassian.net/browse/PP-1698
   * - When a user deselects all invoices, the “Pay invoice(s)” radio button remains selected.
   * - When selecting “Pay without an invoice”, all invoice rows are deselected to reinforce to the user that they are not paying those specific invoices.
   * - If the user selects an invoice while “Pay without an invoice” is selected, the sidebar radio changes back to “Pay invoice(s)”.
   * - If the user toggles back to “Pay invoice(s)”, the previously selected invoices are re-selected.
   */
  selectRow(payable: PayableTableObject): void {
    if (payable.disabled) {
      return;
    }

    const payWithInvoices = this.store.selectSnapshot(PaymentsState.getPayWithInvoices);
    const isPayableAlreadySelected = this.store.selectSnapshot(PaymentsState.getSelectedPayableIds).includes(payable.arTranId);
    if (payWithInvoices && isPayableAlreadySelected) {
      this.store.dispatch(new RemoveArTranAllocations(payable.arTranId));
      return;
    }

    const arTranAllocation: ArTranAllocation = {
      arTranId: payable.arTranId,
      allocatedAmount: payable.amountRemaining,
      active: payable.invoice.arTran.active,
    };

    if (payWithInvoices) {
      this.store.dispatch(new UpdateArTranAllocations(arTranAllocation));
    } else {
      this.store.dispatch(new SetArTranAllocations(arTranAllocation));
    }
  }

  /**
   * Destroy Subscriptions.
   */
  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
